import { logger } from '@/services/loggerService'
import { getFunctions, httpsCallable } from 'firebase/functions'

class OpenAIService {
  constructor() {
    this.functions = getFunctions()
    this.askContractQuestionFn = httpsCallable(this.functions, 'askContractQuestion')
    this.extractPdfTextFn = httpsCallable(this.functions, 'extractContractPdfText')
    this.generateFormSectionsFn = httpsCallable(this.functions, 'generateFormSectionsV2', { timeout: 300000 })
    this.generateBlueprintFn = httpsCallable(this.functions, 'generateBlueprint', { timeout: 300000 })
    this.refineBlueprintSectionFn = httpsCallable(this.functions, 'refineBlueprintSection', { timeout: 120000 })
    this.generateDiscussionGuideFn = httpsCallable(this.functions, 'generateDiscussionGuide', { timeout: 300000 })
    this.generateEpisodeContentFn = httpsCallable(this.functions, 'generateEpisodeContent', { timeout: 300000 })
    this.generateAutomationRuleFn = httpsCallable(this.functions, 'generateAutomationRule', { timeout: 300000 })
    this.pdfTextCache = new Map() // Cache PDF text in memory
  }

  /**
   * Generate an episode's title, on-thumbnail text, and show notes from a
   * transcript in one coherent pass.
   * @param {string} projectId - the episode project id. The function reads
   *   episodeTranscripts/{projectId} itself so the word-level timings stored with
   *   the text come along; those are what keep the timestamps real.
   * @param {string} prompt - the resolved system prompt (show override || global default)
   * @param {Object} [showContext] - { showId, showName, episodeNumber, episodeName }. The
   *   function appends the show's aiTitleFormat / aiRules server-side from showId.
   * @returns {Promise<{title: string, thumbnailText: string, showNotes: string, tokensUsed: number}>}
   */
  async generateEpisodeContent(projectId, prompt, showContext = {}) {
    try {
      const result = await this.generateEpisodeContentFn({ projectId, prompt, ...showContext })
      const d = result.data
      if (!d?.success) throw new Error('Invalid response from generateEpisodeContent')
      return {
        title: d.title || '',
        thumbnailText: d.thumbnailText || '',
        showNotes: d.showNotes || '',
        tokensUsed: d.tokensUsed || 0,
      }
    } catch (error) {
      const code = (error?.code || '').replace('functions/', '')
      logger.error('[OpenAIService] generateEpisodeContent failed', { code, error: error.message })
      throw new Error(
        code === 'deadline-exceeded' ? 'Generation timed out. Please try again.'
          : code === 'resource-exhausted' ? 'AI is rate-limited. Please try again shortly.'
            // failed-precondition is the actionable one (no transcript yet, no API
            // key), so show what the function actually said.
            : code === 'failed-precondition' ? error.message
              : 'Failed to generate episode content.'
      )
    }
  }

  /**
   * Generate a deliverable automation rule from a natural-language description.
   *
   * Returns a rule shaped exactly like the builder's buildRuleData() output, so the caller
   * can hand it straight to loadRuleIntoForm(). Nothing is persisted — the admin reviews the
   * filled-in form and clicks Save.
   *
   * @param {string} prompt - what the rule should do, in plain English
   * @param {Object} [ctx]
   * @param {Object|null} [ctx.currentRule] - the rule open in the builder, to modify rather than replace
   * @param {Array} [ctx.conversationHistory] - prior turns, for follow-ups like "make it 2 days"
   * @param {Array} [ctx.users] - [{ id, name }] so a name in the prompt resolves to a user id
   * @param {Array} [ctx.projects] - [{ id, name, companyName }]
   * @param {Array} [ctx.existingRules] - live rules, so the model follows house conventions
   * @returns {Promise<{rule: Object, summary: string, warnings: string[], tokensUsed: number}>}
   */
  async generateAutomationRule(prompt, { currentRule = null, conversationHistory = [], users = [], projects = [], existingRules = [] } = {}) {
    try {
      const result = await this.generateAutomationRuleFn({
        prompt, currentRule, conversationHistory, users, projects, existingRules
      })
      const d = result.data
      if (!d?.success || !d.rule) throw new Error('Invalid response from generateAutomationRule')
      logger.info('[OpenAIService] Automation rule generated', {
        warnings: d.warnings?.length || 0, tokensUsed: d.tokensUsed
      })
      return {
        rule: d.rule,
        summary: d.summary || '',
        warnings: d.warnings || [],
        tokensUsed: d.tokensUsed || 0,
      }
    } catch (error) {
      const code = (error?.code || '').replace('functions/', '')
      logger.error('[OpenAIService] generateAutomationRule failed', { code, error: error.message })
      throw new Error(
        code === 'deadline-exceeded' ? 'Generation timed out. Please try again.'
          : code === 'resource-exhausted' ? 'AI is rate-limited. Please try again shortly.'
            // failed-precondition covers a missing API key and a declined request — both
            // are actionable, so show what the function actually said.
            : code === 'failed-precondition' ? error.message
              : 'Failed to generate the rule.'
      )
    }
  }

  /**
   * Ask a question about a contract using Firebase Function
   * @param {string} contractContent - The contract text or key details
   * @param {string} question - The user's question
   * @param {Array} conversationHistory - Previous messages in the conversation
   * @returns {Promise<string>} - The assistant's response
   */
  async askAboutContract(contractContent, question, conversationHistory = []) {
    try {
      const result = await this.askContractQuestionFn({
        contractInfo: contractContent,
        question: question,
        conversationHistory: conversationHistory
      })

      if (result.data?.success && result.data?.answer) {
        logger.info('[OpenAIService] Contract question processed successfully', {
          tokensUsed: result.data.tokensUsed
        })
        return result.data.answer
      } else {
        throw new Error('Invalid response from contract question function')
      }
    } catch (error) {
      logger.error('[OpenAIService] Failed to ask about contract', {
        error: error.message,
        code: error.code
      })
      
      // Provide user-friendly error messages
      if (error.code === 'unauthenticated') {
        throw new Error('You must be logged in to use this feature')
      } else if (error.code === 'failed-precondition') {
        throw new Error('AI assistant is not configured. Contact your administrator.')
      } else if (error.code === 'resource-exhausted') {
        throw new Error('Too many requests. Please try again later.')
      } else {
        throw new Error('Failed to process your question. Please try again.')
      }
    }
  }

  /**
   * Extract text from a contract PDF
   * @param {string} pdfUrl - The URL of the PDF to extract text from
   * @param {string} contractId - Optional contract ID for caching
   * @returns {Promise<string>} - The extracted text
   */
  async extractPdfText(pdfUrl, contractId = null) {
    try {
      // Check memory cache first
      const cacheKey = contractId || pdfUrl
      if (this.pdfTextCache.has(cacheKey)) {
        logger.info('[OpenAIService] Using cached PDF text', { cacheKey })
        return this.pdfTextCache.get(cacheKey)
      }

      const result = await this.extractPdfTextFn({
        pdfUrl: pdfUrl,
        contractId: contractId
      })

      if (result.data?.success && result.data?.text) {
        // Cache the text in memory
        this.pdfTextCache.set(cacheKey, result.data.text)
        
        logger.info('[OpenAIService] PDF text extracted successfully', {
          cached: result.data.cached,
          pageCount: result.data.pageCount,
          textLength: result.data.text.length
        })
        
        return result.data.text
      } else {
        throw new Error('Failed to extract PDF text')
      }
    } catch (error) {
      logger.error('[OpenAIService] Failed to extract PDF text', {
        error: error.message,
        code: error.code
      })
      
      // Return empty string on error so chat can still function with metadata
      return ''
    }
  }

  /**
   * Generate form sections from a natural language prompt
   * @param {string} prompt - The user's description of the form
   * @param {Array} existingSections - Current form sections for context
   * @param {Array} conversationHistory - Previous messages in the conversation
   * @returns {Promise<Object>} - { sections, summary }
   */
  async generateFormSections(prompt, existingSections = null, conversationHistory = []) {
    try {
      const result = await this.generateFormSectionsFn({
        prompt,
        existingSections,
        conversationHistory
      })

      if (result.data?.success && result.data?.sections) {
        logger.info('[OpenAIService] Form sections generated successfully', {
          sectionsCount: result.data.sections.length,
          tokensUsed: result.data.tokensUsed
        })
        return {
          sections: result.data.sections,
          summary: result.data.summary || ''
        }
      } else {
        throw new Error('Invalid response from form generation function')
      }
    } catch (error) {
      logger.error('[OpenAIService] Failed to generate form sections', {
        error: error.message,
        code: error.code
      })

      if (error.code === 'unauthenticated') {
        throw new Error('You must be logged in to use this feature')
      } else if (error.code === 'permission-denied') {
        throw new Error('Only admin users can generate forms')
      } else if (error.code === 'failed-precondition') {
        throw new Error('AI assistant is not configured. Contact your administrator.')
      } else if (error.code === 'resource-exhausted') {
        throw new Error('Too many requests. Please try again later.')
      } else {
        throw new Error('Failed to generate form. Please try again.')
      }
    }
  }

  /**
   * Generate a podcast blueprint strategy
   * @param {string} showId - The show document ID
   * @param {string} transcript - Workshop transcript text
   * @param {string|null} feedbackNotes - Optional revision notes for iteration
   * @param {Array|null} onetimeReferenceDocs - Optional one-time reference docs [{title, content}]
   * @param {Array|null} excludedRefDocIds - IDs of saved reference docs to exclude
   * @returns {Promise<Object>} - { markdown, tokensUsed }
   */
  async generateBlueprint(showId, transcript, feedbackNotes = null, onetimeReferenceDocs = null, excludedRefDocIds = null) {
    try {
      const payload = { showId, transcript }
      if (feedbackNotes) payload.feedbackNotes = feedbackNotes
      if (onetimeReferenceDocs) payload.onetimeReferenceDocs = onetimeReferenceDocs
      if (excludedRefDocIds) payload.excludedRefDocIds = excludedRefDocIds

      const result = await this.generateBlueprintFn(payload)

      if (result.data?.success && result.data?.markdown) {
        logger.info('[OpenAIService] Blueprint generated successfully', {
          showId,
          tokensUsed: result.data.tokensUsed
        })
        return {
          markdown: result.data.markdown,
          tokensUsed: result.data.tokensUsed || 0
        }
      } else {
        throw new Error('Invalid response from blueprint generation function')
      }
    } catch (error) {
      logger.error('[OpenAIService] Failed to generate blueprint', {
        error: error.message,
        code: error.code,
        showId
      })

      if (error.code === 'unauthenticated') {
        throw new Error('You must be logged in to use this feature')
      } else if (error.code === 'permission-denied') {
        throw new Error('Only admin users can generate blueprints')
      } else if (error.code === 'failed-precondition') {
        throw new Error(error.message || 'AI assistant is not configured. Contact your administrator.')
      } else if (error.code === 'resource-exhausted') {
        throw new Error('Too many requests. Please try again later.')
      } else if (error.code === 'deadline-exceeded') {
        throw new Error('Generation timed out. Please try again.')
      } else {
        throw new Error('Failed to generate blueprint. Please try again.')
      }
    }
  }

  /**
   * Refine a specific section of a blueprint
   * @param {string} selectedText - The text passage to rewrite
   * @param {string} instruction - What to change
   * @param {string} fullBlueprint - Full blueprint markdown for context
   * @returns {Promise<Object>} - { revisedText, tokensUsed }
   */
  async refineBlueprintSection(selectedText, instruction, fullBlueprint) {
    try {
      const result = await this.refineBlueprintSectionFn({
        selectedText,
        instruction,
        fullBlueprint
      })

      if (result.data?.success && result.data?.revisedText) {
        logger.info('[OpenAIService] Blueprint section refined', {
          tokensUsed: result.data.tokensUsed
        })
        return {
          revisedText: result.data.revisedText,
          tokensUsed: result.data.tokensUsed || 0
        }
      } else {
        throw new Error('Invalid response from section refinement function')
      }
    } catch (error) {
      logger.error('[OpenAIService] Failed to refine blueprint section', {
        error: error.message,
        code: error.code
      })

      if (error.code === 'unauthenticated') {
        throw new Error('You must be logged in to use this feature')
      } else if (error.code === 'permission-denied') {
        throw new Error('Only admin users can refine blueprints')
      } else if (error.code === 'resource-exhausted') {
        throw new Error('Too many requests. Please try again later.')
      } else if (error.code === 'deadline-exceeded') {
        throw new Error('Refinement timed out. Please try again.')
      } else {
        throw new Error('Failed to refine section. Please try again.')
      }
    }
  }

  /**
   * Clear cached PDF text
   * @param {string} contractId - Optional contract ID to clear specific cache
   */
  clearPdfTextCache(contractId = null) {
    if (contractId) {
      this.pdfTextCache.delete(contractId)
    } else {
      this.pdfTextCache.clear()
    }
  }

  /**
   * Generate a discussion guide from episode data and prompt
   * @param {string} showId - Show ID
   * @param {string} episodeData - Formatted questionnaire responses
   * @param {string} prompt - System prompt for generation
   * @param {string|null} feedbackNotes - Revision feedback
   * @param {Array|null} onetimeReferenceDocs - One-time reference docs
   * @param {Array|null} excludedRefDocIds - IDs of global docs to exclude
   * @param {string} model - AI model to use
   * @param {Array|null} attachmentDocuments - Files uploaded in the questionnaire,
   *   each `{ name, downloadURL, mimeType?, size? }`. Cloud Function fetches and
   *   passes them to Claude as native document blocks (PDFs/images/text).
   * @param {string|null} episodeId - Required for client (non-admin) callers: the
   *   function authorises against this episode and derives the attachments from it.
   * @returns {Promise<Object>} { markdown, tokensUsed }
   */
  async generateDiscussionGuide(showId, episodeData, prompt, feedbackNotes = null, onetimeReferenceDocs = null, excludedRefDocIds = null, model = 'claude-sonnet-4-6', attachmentDocuments = null, episodeId = null) {
    try {
      const payload = { showId, episodeData, prompt, model }
      // Client callers are authorised against the episode server-side; the
      // function ignores the prompt/model/attachments they send and re-derives
      // them from this episode. Admin callers don't need it.
      if (episodeId) payload.episodeId = episodeId
      if (feedbackNotes) payload.feedbackNotes = feedbackNotes
      if (onetimeReferenceDocs) payload.onetimeReferenceDocs = onetimeReferenceDocs
      if (excludedRefDocIds) payload.excludedRefDocIds = excludedRefDocIds
      if (Array.isArray(attachmentDocuments) && attachmentDocuments.length) {
        payload.attachmentDocuments = attachmentDocuments
      }

      const result = await this.generateDiscussionGuideFn(payload)

      if (result.data?.success && result.data?.markdown) {
        logger.info('[OpenAIService] Discussion guide generated', {
          showId,
          tokensUsed: result.data.tokensUsed
        })
        return {
          markdown: result.data.markdown,
          tokensUsed: result.data.tokensUsed || 0
        }
      } else {
        throw new Error('Invalid response from discussion guide generation function')
      }
    } catch (error) {
      logger.error('[OpenAIService] Failed to generate discussion guide', {
        error: error.message,
        code: error.code,
        showId
      })

      // Firebase SDK prefixes error codes with 'functions/' — check both formats
      const code = error.code?.replace('functions/', '') || ''
      if (code === 'unauthenticated') {
        throw new Error('You must be logged in to use this feature')
      } else if (code === 'permission-denied') {
        throw new Error('Only admin users can generate discussion guides')
      } else if (code === 'failed-precondition') {
        throw new Error(error.message || 'AI assistant is not configured. Contact your administrator.')
      } else if (code === 'resource-exhausted') {
        throw new Error(error.message || 'AI service is temporarily overloaded. Please try again in a moment.')
      } else if (code === 'deadline-exceeded') {
        throw new Error('Generation timed out. Please try again.')
      } else {
        throw new Error(error.message || 'Failed to generate discussion guide. Please try again.')
      }
    }
  }

  /**
   * Check if the service is configured (always returns true for Firebase Functions)
   * @returns {boolean}
   */
  isConfigured() {
    // Since we're using Firebase Functions, configuration is handled server-side
    // Return true to indicate the service is available
    return true
  }
}

export const openAIService = new OpenAIService()